import { type JSX, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  Complex,
  ComplexesControlType,
} from '@features/effector-form/controls/complexes/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { ComplexesControlGate, complexesQuery } from './model';
import * as Styled from './styled';

export const ComplexesControl = withControl<ComplexesControlType>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    placeholder,
    isError,
  }) => {
    useGate(ComplexesControlGate);
    const { t } = useTranslation();

    const { data, pending } = useUnit(complexesQuery);

    const options = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const getValue = useCallback(
      () =>
        options.filter(
          ({ id }) =>
            value.includes(id) ||
            value.find((complex: { id: number }) => complex?.id === id)
        ),
      [options, value]
    );

    const values = getValue();

    return (
      <Styled.CustomFormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          multiple
          disableCloseOnSelect
          loading={pending}
          value={values}
          options={options}
          getOptionKey={(option) => option.id}
          onChange={(event: any, newValue: any) => {
            setValue(newValue.map((item: Complex) => item.id));
          }}
          size="small"
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  placeholder: !readOnly && values.length === 0 ? placeholder : undefined,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
          loadingText={t('Loading')}
          noOptionsText={t('DataNotFound')}
        />
        {!readOnly && error && <FormHelperText error>{error}</FormHelperText>}
      </Styled.CustomFormControl>
    );
  }
);
